import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLang } from '../context/LangContext';

const content = {
  en: {
    pageTitle: 'Privacy Policy',
    metaTitle: 'Privacy Policy | Halo Aesthetic Clinic Richmond Hill',
    metaDesc: 'Read the Privacy Policy of Halo Aesthetic Clinic in Richmond Hill, ON regarding patient data protection, consultations, and confidentiality.',
    lastUpdated: 'Last Updated: January 2026',
    sections: [
      {
        title: 'Introduction',
        content: 'Halo Aesthetic Clinic ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.'
      },
      {
        title: 'Information We Collect',
        content: 'We may collect personal information that you voluntarily provide to us when you:',
        list: [
          'Schedule appointments or consultations',
          'Fill out contact forms or questionnaires',
          'Subscribe to our newsletter or promotional materials',
          'Participate in surveys or feedback forms',
          'Communicate with us via phone, email, or in-person'
        ],
        additionalContent: 'This information may include your name, email address, phone number, date of birth, medical history, treatment preferences, and payment information.'
      },
      {
        title: 'How We Use Your Information',
        content: 'We use the collected information for the following purposes:',
        list: [
          'To provide and improve our aesthetic services',
          'To schedule and manage appointments',
          'To communicate with you about your treatments',
          'To send you relevant health and safety information',
          'To process payments and billing',
          'To comply with legal and regulatory requirements',
          'To improve our website and services'
        ]
      },
      {
        title: 'Information Sharing and Disclosure',
        content: 'We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except in the following circumstances:',
        list: [
          'With your explicit consent',
          'To comply with legal obligations or court orders',
          'To protect our rights, property, or safety',
          'With trusted service providers who assist in our operations (under strict confidentiality agreements)',
          'In case of a business transfer or merger'
        ]
      },
      {
        title: 'Data Security',
        content: 'We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure.'
      },
      {
        title: 'Your Rights',
        content: 'You have the right to:',
        list: [
          'Access and review your personal information',
          'Request corrections to inaccurate information',
          'Request deletion of your personal information',
          'Withdraw consent for data processing',
          'Request data portability',
          'Lodge a complaint with relevant authorities'
        ]
      },
      {
        title: 'Refund & Cancellation Policy (Medical Aesthetic Services)',
        content: 'Refund Policy: All medical aesthetic services are provided under a written treatment service agreement. Services that have already been rendered are non-refundable. For treatment packages, any unused and unrendered services may be eligible for a refund or credit. Cancellation Policy: Clients may cancel future appointments with a minimum of 24 hours\' notice. Missed or late-cancelled appointments may be subject to a cancellation fee. This policy does not affect a client\'s statutory rights under applicable consumer protection laws.',
        list: [
          'Refund requests must be submitted in writing; refunds apply only to the unused portion of the treatment package.',
          'An administrative fee may apply where permitted by law.',
          'Approved refunds will be processed to the original form of payment within 10–14 business days.',
          'Clients may cancel with at least 24 hours\' notice; late or missed appointments may incur a cancellation fee.'
        ]
      },
      {
        title: 'Cookies and Tracking Technologies',
        content: 'Our website may use cookies and similar tracking technologies to enhance your browsing experience, analyze website traffic, and personalize content. You can control cookie settings through your browser preferences.'
      },
      {
        title: 'Third-Party Links',
        content: 'Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review their privacy policies.'
      },
      {
        title: 'Children\'s Privacy',
        content: 'Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children under 18. If we become aware that we have collected such information, we will take steps to delete it promptly.'
      },
      {
        title: 'Changes to This Policy',
        content: 'We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.'
      },
      {
        title: 'Contact Us',
        content: 'If you have any questions about this Privacy Policy or our privacy practices, please contact us at:',
        contact: {
          email: 'info@haloaestheticclinic.com',
          phone: '942-388-1688',
          address: '1595 16th Ave Suite 103, Richmond Hill, ON L4B 3N9'
        }
      }
    ]
  },
  cn: {
    pageTitle: '隐私政策',
    metaTitle: '隐私政策 | Halo Richmond Hill 医美诊所',
    metaDesc: 'Halo Richmond Hill 医美诊所关于客户隐私保护、个人数据安全与面诊保密规范的说明。',
    lastUpdated: '最后更新：2026年1月',
    sections: [
      {
        title: '引言',
        content: 'Halo Aesthetic Clinic（"我们"、"我们的"或"我们"）致力于保护您的隐私。本隐私政策说明了当您访问我们的网站或使用我们的服务时，我们如何收集、使用、披露和保护您的信息。'
      },
      {
        title: '我们收集的信息',
        content: '当您进行以下活动时，我们可能会收集您自愿提供的个人信息：',
        list: [
          '预约或咨询',
          '填写联系表格或问卷',
          '订阅我们的新闻通讯或促销材料',
          '参与调查或反馈表单',
          '通过电话、电子邮件或面对面与我们沟通'
        ],
        additionalContent: '这些信息可能包括您的姓名、电子邮件地址、电话号码、出生日期、医疗历史、治疗偏好和付款信息。'
      },
      {
        title: '我们如何使用您的信息',
        content: '我们将收集的信息用于以下目的：',
        list: [
          '提供和改进我们的医美服务',
          '安排和管理预约',
          '就您的治疗与您沟通',
          '向您发送相关的健康和安全信息',
          '处理付款和账单',
          '遵守法律和监管要求',
          '改进我们的网站和服务'
        ]
      },
      {
        title: '信息共享和披露',
        content: '未经您的同意，我们不会向第三方出售、交易或以其他方式转让您的个人信息，除非在以下情况下：',
        list: [
          '获得您的明确同意',
          '遵守法律义务或法院命令',
          '保护我们的权利、财产或安全',
          '与协助我们运营的可信服务提供商（在严格的保密协议下）',
          '在业务转让或合并的情况下'
        ]
      },
      {
        title: '数据安全',
        content: '我们实施适当的技术和组织安全措施，以保护您的个人信息免受未经授权的访问、更改、披露或破坏。但是，通过互联网传输或电子存储的方法都不是100%安全的。'
      },
      {
        title: '您的权利',
        content: '您有权：',
        list: [
          '访问和查看您的个人信息',
          '要求更正不准确的信息',
          '要求删除您的个人信息',
          '撤回数据处理同意',
          '要求数据可移植性',
          '向相关当局提出投诉'
        ]
      },
      {
        title: '退款与取消政策（医疗美容服务）',
        content: '退款政策：所有医疗美容服务均在签署书面疗程服务协议的前提下提供。已完成并已提供的服务不予退款。对于疗程套餐，任何尚未使用且尚未提供的服务可在符合条款下申请退款或转为服务额度。取消政策：客户须至少提前24小时通知取消预约；未按时取消或缺席可能产生取消费用。本政策不影响客户依据适用的消费者保护法律所享有的法定权利。',
        list: [
          '退款申请须以书面形式提交；退款仅适用于疗程套餐中未使用的部分。',
          '在法律允许的范围内可能收取合理的行政处理费用。',
          '经批准的退款将在10–14个工作日内原路退回至原付款方式。',
          '客户须至少提前24小时取消预约；延迟取消或缺席可能产生取消费用。'
        ]
      },
      {
        title: 'Cookie和跟踪技术',
        content: '我们的网站可能使用Cookie和类似的跟踪技术来增强您的浏览体验、分析网站流量并个性化内容。您可以通过浏览器首选项控制Cookie设置。'
      },
      {
        title: '第三方链接',
        content: '我们的网站可能包含指向第三方网站的链接。我们不对这些外部网站的隐私做法或内容负责。我们鼓励您查看他们的隐私政策。'
      },
      {
        title: '儿童隐私',
        content: '我们的服务不针对18岁以下的个人。我们不会故意收集18岁以下儿童的个人信息。如果我们发现收集了此类信息，我们将采取措施立即删除。'
      },
      {
        title: '政策变更',
        content: '我们可能会不时更新此隐私政策。我们将通过在此页面上发布新的隐私政策并更新"最后更新"日期来通知您任何重大变更。'
      },
      {
        title: '联系我们',
        content: '如果您对此隐私政策或我们的隐私做法有任何疑问，请通过以下方式联系我们：',
        contact: {
          email: 'info@haloaestheticclinic.com',
          phone: '942-388-1688',
          address: '1595 16th Ave Suite 103, Richmond Hill, ON L4B 3N9'
        }
      }
    ]
  }
};

const PrivacyPolicy = () => {
  const { lang } = useLang();
  const c = content[lang];

  const canonicalUrl = 'https://www.haloaestheticclinic.com/privacy-policy';

  return (
    <div className="bg-softGradient bg-no-repeat bg-top text-text">
      <Helmet>
        <title>{c.metaTitle}</title>
        <meta name="description" content={c.metaDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={c.metaTitle} />
        <meta property="og:description" content={c.metaDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
      </Helmet>
      {/* 页面标题 */}
      <section className="py-section max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 text-gray-900">{c.pageTitle}</h1>
          <p className="text-lg text-gray-600">{c.lastUpdated}</p>
        </div>

        {/* 内容区域 */}
        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-card p-8 border border-champagne/40">
          <div className="space-y-8">
            {c.sections.map((section, index) => (
              <div key={index} className="border-b border-champagne/30 pb-6 last:border-b-0">
                <h2 className="text-2xl font-bold text-[#2E6B4C] mb-4">{section.title}</h2>
                <p className="text-gray-700 mb-4 leading-relaxed">{section.content}</p>
                
                {section.list && (
                  <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                    {section.list.map((item, itemIndex) => (
                      <li key={itemIndex} className="leading-relaxed">{item}</li>
                    ))}
                  </ul>
                )}
                
                {section.additionalContent && (
                  <p className="text-gray-700 mt-4 leading-relaxed">{section.additionalContent}</p>
                )}
                
                {section.contact && (
                  <div className="mt-4 p-4 bg-softGold/20 rounded-lg border border-champagne/40">
                    <p className="text-gray-700 mb-2"><strong>Email:</strong> {section.contact.email}</p>
                    <p className="text-gray-700 mb-2"><strong>Phone:</strong> <a href="tel:942-388-1688" className="hover:text-[#2E6B4C] transition-colors">{section.contact.phone}</a></p>
                    <p className="text-gray-700"><strong>Address:</strong> {section.contact.address}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
